import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle, AlertCircle, Link } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VerificarStatusClicksignProps {
  onVerificacaoCompleta?: () => void;
}

export default function VerificarStatusClicksign({ onVerificacaoCompleta }: VerificarStatusClicksignProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleVerificarStatus = async () => {
    setIsVerifying(true);
    
    try {
      console.log('🔍 Iniciando verificação de status do ClickSign...');
      
      const { data, error } = await supabase.functions.invoke('verificar-status-clicksign', {
        body: {}
      });

      if (error) {
        console.error('❌ Erro na verificação:', error);
        toast.error('Erro ao verificar status dos documentos');
        return;
      }

      console.log('✅ Verificação concluída:', data);
      
      if (data.success) {
        toast.success(
          `Verificação concluída! ${data.propostas_verificadas} propostas e ${data.contratos_verificados} contratos atualizados.`
        );
        
        if (onVerificacaoCompleta) {
          onVerificacaoCompleta();
        }
      } else {
        toast.error('Erro na verificação de status');
      }
      
    } catch (error) {
      console.error('❌ Erro na verificação:', error);
      toast.error('Erro interno na verificação');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSincronizarDocumentos = async () => {
    setIsSyncing(true);
    
    try {
      console.log('🔄 Iniciando sincronização de documentos ClickSign...');
      
      const { data, error } = await supabase.functions.invoke('sincronizar-clicksign-por-email', {
        body: {}
      });

      if (error) {
        console.error('❌ Erro na sincronização:', error);
        toast.error('Erro ao sincronizar documentos');
        return;
      }

      console.log('✅ Sincronização concluída:', data);
      
      if (data.success) {
        toast.success(
          `Sincronização concluída! ${data.sincronizados} documentos vinculados${data.erros > 0 ? `, ${data.erros} erros` : ''}.`
        );
        
        if (onVerificacaoCompleta) {
          onVerificacaoCompleta();
        }
      } else {
        toast.error('Erro na sincronização');
      }
      
    } catch (error) {
      console.error('❌ Erro na sincronização:', error);
      toast.error('Erro interno na sincronização');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleSincronizarDocumentos}
          disabled={isSyncing || isVerifying}
          className="flex items-center gap-2"
        >
          {isSyncing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Sincronizando...
            </>
          ) : (
            <>
              <Link className="h-4 w-4" />
              Sincronizar Documentos
            </>
          )}
        </Button>
        
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleVerificarStatus}
          disabled={isVerifying || isSyncing}
          className="flex items-center gap-2"
        >
          {isVerifying ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Verificando...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Verificar Status
            </>
          )}
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Sincronize documentos órfãos e verifique assinaturas pendentes
      </div>
    </div>
  );
}
